import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import { 
  XMarkIcon, 
  BuildingOfficeIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const BranchAssignmentModal = ({ 
  isOpen, 
  onClose, 
  selectedLeads = [], 
  onAssignmentComplete 
}) => {
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadBranches();
      setSelectedBranch('');
      setError('');
    }
  }, [isOpen]);

  const loadBranches = async () => {
    setLoadingBranches(true);
    try {
      const token = localStorage.getItem('companyToken') || 
                    localStorage.getItem('authToken') || 
                    localStorage.getItem('token');
      
      const response = await axios.get('/branches', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setBranches(response.data.data || []);
      }
      setLoadingBranches(false);
    } catch (error) {
      console.error('Error loading branches:', error);
      setError('Failed to load branches');
      setLoadingBranches(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedBranch) {
      setError('Please select a branch');
      return;
    }

    if (selectedLeads.length === 0) {
      setError('No leads selected');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('companyToken') || 
                    localStorage.getItem('authToken') || 
                    localStorage.getItem('token');

      // Get company ID from localStorage
      const companyInfo = JSON.parse(localStorage.getItem('companyInfo') || '{}');
      const companyId = companyInfo.id || companyInfo._id;

      // Update each lead with branch assignment
      const updatePromises = selectedLeads.map(lead => 
        axios.put(
          `/leads/${lead._id}`,
          {
            assignedBranch: selectedBranch,
            company: companyId
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        )
      );

      await Promise.all(updatePromises);

      onAssignmentComplete({
        success: true,
        message: `Successfully assigned ${selectedLeads.length} lead(s) to branch`
      });

      onClose();
    } catch (error) {
      console.error('Error assigning leads to branch:', error);
      setError('Failed to assign leads to branch');
      onAssignmentComplete({
        success: false,
        error: 'Failed to assign leads to branch'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <BuildingOfficeIcon className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-900">
              Assign Leads to Branch
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Selected Leads Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <CheckCircleIcon className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-blue-900">
                {selectedLeads.length} lead(s) selected for assignment
              </span>
            </div>
            <div className="mt-2 text-xs text-blue-700">
              {selectedLeads.slice(0, 3).map(lead => (
                <div key={lead._id}>
                  • {lead.name || lead.customerName} - {lead.companyName || lead.customerCompany}
                </div>
              ))}
              {selectedLeads.length > 3 && (
                <div className="text-blue-600 font-medium mt-1">
                  + {selectedLeads.length - 3} more...
                </div>
              )}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
                <span className="text-sm font-medium text-red-900">{error}</span>
              </div>
            </div>
          )}

          {/* Branch Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Branch *
            </label>
            {loadingBranches ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : branches.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <BuildingOfficeIcon className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                <p>No branches available</p>
                <p className="text-xs mt-1">Please create branches first</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-2">
                {branches.map((branch) => (
                  <label
                    key={branch._id}
                    className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedBranch === branch._id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="branch"
                      value={branch._id}
                      checked={selectedBranch === branch._id}
                      onChange={(e) => setSelectedBranch(e.target.value)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="ml-3 flex-1">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-900">
                          {branch.name}
                        </span>
                        {branch.branchCode && (
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                            {branch.branchCode}
                          </span>
                        )}
                      </div>
                      {branch.city && (
                        <span className="text-xs text-gray-600">
                          📍 {branch.city}
                        </span>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Assignment Info */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">
              What happens after assignment?
            </h4>
            <ul className="text-xs text-gray-600 space-y-1">
              <li>✓ Leads will be assigned to the selected branch</li>
              <li>✓ Branch can view and manage these leads</li>
              <li>✓ You can track branch performance from Branch Tracking</li>
              <li>✓ Follow-ups by branch will be visible to you</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleAssign}
            disabled={loading || !selectedBranch || loadingBranches}
            className="px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Assigning...
              </>
            ) : (
              <>
                <CheckCircleIcon className="w-4 h-4 mr-2" />
                Assign to Branch
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BranchAssignmentModal;
